import {
   ArrowLeftMdIcon,
   Building01Icon,
   ChevronRightIcon,
   MapPinIcon,
} from '@forwardflow/icons'
import { useOnboardingStoreV2 } from '@forwardflow/store/onboardingStoreV2'
import { IBusinessDetails } from '@forwardflow/types/onboarding'
import {
   Button,
   ContentCard,
   Grid,
   Group,
   Radio,
   Select,
   Stack,
   TextInput,
} from '@forwardflow/ui-v2'
import { useForm } from '@mantine/form'
import { motion } from 'framer-motion'

export const BusinessDetails = () => {
   const { getNextStep, getPreviousStep, onboardingData, setBusinessDetails } =
      useOnboardingStoreV2()
   const { businessDetails } = onboardingData

   const form = useForm<IBusinessDetails>({
      initialValues: {
         legalBusinessName: businessDetails?.legalBusinessName || '',
         businessType: businessDetails?.businessType || '',
         businessOwnerName: businessDetails?.businessOwnerName || '',
         businessOwnerTitle: businessDetails?.businessOwnerTitle || '',
         phoneNumber: businessDetails?.phoneNumber || '',
         email: businessDetails?.email || '',
         websiteUrl: businessDetails?.websiteUrl || '',
         businessEIIN: businessDetails?.businessEIIN || '',
         street: businessDetails?.street || '',
         city: businessDetails?.city || '',
         state: businessDetails?.state || '',
         postalCode: businessDetails?.postalCode || '',
         businessTeamName: businessDetails?.businessTeamName || '',
         businessBrokerageName: businessDetails?.businessBrokerageName || '',
         hasTeam: businessDetails?.hasTeam || false,
      },
      validate: {
         legalBusinessName: (value) =>
            !value ? 'Legal business name is required' : null,
         businessType: (value) => (!value ? 'Business type is required' : null),
         businessOwnerName: (value) => {
            if (!value) return 'Business owner name is required'
            const nameRegex = /^[a-zA-Z\s.]+$/
            if (!nameRegex.test(value)) {
               return 'Business Owner Name can only consist alphabets, period or space.'
            }
            return null
         },
         businessOwnerTitle: (value) =>
            !value ? 'Business owner title is required' : null,
         phoneNumber: (value) => {
            if (!value) return 'Phone number is required'
            const cleanPhone = value.replace(/[\s\-()]/g, '')
            if (!/^\d+$/.test(cleanPhone)) {
               return 'Phone number should only contain digits.'
            }
            if (cleanPhone.length !== 10) {
               return 'Enter a valid 10-digit phone number.'
            }
            return null
         },
         email: (value) => {
            if (!value) return 'Email is required'
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
            if (!emailRegex.test(value)) {
               return 'Enter a valid email address.'
            }
            return null
         },
         websiteUrl: (value) => {
            if (!value) return 'Website URL is required'
            if (value.includes(' ')) {
               return 'Enter a valid website URL.'
            }
            const urlRegex =
               /^(https?:\/\/)?([\w-]+\.)+[\w-]+(\/[\w- ./?%&=]*)?$/
            if (!urlRegex.test(value)) {
               return 'Enter a valid website URL.'
            }
            return null
         },
         businessEIIN: (value) => {
            if (!value) return 'Business EIN is required'
            const cleanEIN = value.replace(/[-\s]/g, '')
            if (!/^\d+$/.test(cleanEIN)) {
               return 'Enter a valid 9-digit EIN.'
            }
            if (cleanEIN.length !== 9) {
               return 'Enter a valid 9-digit EIN.'
            }
            return null
         },
         street: (value) => (!value ? 'Street address is required' : null),
         city: (value) => (!value ? 'City is required' : null),
         state: (value) => (!value ? 'State is required' : null),
         postalCode: (value) => {
            if (!value) return 'ZIP code is required'
            if (!/^\d+$/.test(value)) {
               return 'ZIP should only be numbers, and 5 digits.'
            }
            if (value.length !== 5) {
               return 'ZIP should only be numbers, and 5 digits.'
            }
            return null
         },
         businessBrokerageName: (value) =>
            !value ? 'Brokerage name is required' : null,
      },
      validateInputOnBlur: true,
   })

   const handleSubmit = (values: IBusinessDetails) => {
      setBusinessDetails({
         ...businessDetails,
         ...values,
      })
      getNextStep()
   }

   return (
      <Stack gap={48}>
         <ContentCard
            className="space-y-2"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
         >
            <p className="text-white font-medium">
               Tell us a bit about your business
            </p>
            <p className="text-[#99A1AF] text-sm font-medium max-w-[600px]">
               Share your basic business details so we can tailor your Forward
               Flow experience
            </p>
         </ContentCard>
         <Stack gap={24}>
            <ContentCard
               className="space-y-4"
               initial={{ opacity: 0, y: 30 }}
               animate={{ opacity: 1, y: 0 }}
               transition={{ duration: 0.3, delay: 0.6 }}
            >
               <Group>
                  <Group className="p-1.5 px-2.5 rounded-xl border-[0.9px] border-[#8E51FF]/30 bg-gradient-to-br from-[#4F39F6]/30 to-[#7F22FE]/30">
                     <Building01Icon width={20} color="#9F7BFF" />
                  </Group>
                  <p className="text-white font-medium">Business Information</p>
               </Group>

               <Grid>
                  <Grid.Col span={6}>
                     <TextInput
                        label="Legal business name"
                        placeholder="Forward Flow Real Estate LLC"
                        {...form.getInputProps('legalBusinessName')}
                     />
                  </Grid.Col>
                  <Grid.Col span={6}>
                     <Select
                        label="Business Type"
                        placeholder="Select Business Type"
                        {...form.getInputProps('businessType')}
                        data={[
                           'Co-operative',
                           'Corporation',
                           'Limited Liability Corporation',
                           'Non-profit Corporation',
                           'Partnership',
                        ]}
                     />
                  </Grid.Col>
               </Grid>

               <Grid>
                  <Grid.Col span={6}>
                     <TextInput
                        label="Business owner"
                        placeholder="John Smith"
                        {...form.getInputProps('businessOwnerName')}
                     />
                  </Grid.Col>
                  <Grid.Col span={6}>
                     <TextInput
                        label="Your title"
                        placeholder="Agent, Broker, etc."
                        {...form.getInputProps('businessOwnerTitle')}
                     />
                  </Grid.Col>
               </Grid>

               <Grid>
                  <Grid.Col span={6}>
                     <TextInput
                        label="Phone Number"
                        placeholder="415 232 9864"
                        type="tel"
                        {...form.getInputProps('phoneNumber')}
                     />
                  </Grid.Col>
                  <Grid.Col span={6}>
                     <TextInput
                        label="Email"
                        placeholder="brannon@forward-flow.com"
                        type="email"
                        {...form.getInputProps('email')}
                     />
                  </Grid.Col>
               </Grid>

               <Stack>
                  <p className="text-white text-sm font-medium">
                     Are you part of a Team?
                  </p>
                  <Group>
                     <Group className="rounded-lg border border-white/[0.1] bg-white/[0.02] px-4 py-2">
                        <Radio
                           name="hasTeam"
                           value="yes"
                           checked={form.values.hasTeam}
                           onChange={() => form.setFieldValue('hasTeam', true)}
                        />
                        <p className="text-white text-sm font-medium">Yes</p>
                     </Group>

                     <Group className="rounded-lg border border-white/[0.1] bg-white/[0.02] px-4 py-2">
                        <Radio
                           name="hasTeam"
                           value="no"
                           checked={!form.values.hasTeam}
                           onChange={() => form.setFieldValue('hasTeam', false)}
                        />
                        <p className="text-white text-sm font-medium">No</p>
                     </Group>
                  </Group>
               </Stack>

               {form.values.hasTeam && (
                  <TextInput
                     label="Team name"
                     placeholder="Forward Team"
                     {...form.getInputProps('businessTeamName')}
                  />
               )}

               <TextInput
                  label="Brokerage name"
                  placeholder="FF Brokerage"
                  {...form.getInputProps('businessBrokerageName')}
               />

               <TextInput
                  label="Website"
                  placeholder="https://www.ffrealestate.com"
                  {...form.getInputProps('websiteUrl')}
               />

               <TextInput
                  label="Business EIN"
                  placeholder="12-3456789"
                  description="We need your EIN for A2P registration - it's required to send verified text messages. You can skip this for now and add it anytime later from Settings → Business Info."
                  {...form.getInputProps('businessEIIN')}
               />
            </ContentCard>
            <ContentCard
               className="space-y-4"
               initial={{ opacity: 0, y: 30 }}
               animate={{ opacity: 1, y: 0 }}
               transition={{ duration: 0.3, delay: 0.6 }}
            >
               <Group>
                  <Group className="p-1.5 px-2.5 rounded-xl border-[0.9px] border-[#8E51FF]/30 bg-gradient-to-br from-[#4F39F6]/30 to-[#7F22FE]/30">
                     <MapPinIcon width={20} color="#9F7BFF" />
                  </Group>
                  <p className="text-white font-medium">Registration address</p>
               </Group>
               <TextInput
                  label="Street address"
                  placeholder="123 Main Street, Suite 100"
                  autoComplete="street-address"
                  {...form.getInputProps('street')}
               />

               <Grid>
                  <Grid.Col span={4}>
                     <TextInput
                        label="City"
                        placeholder="San Francisco"
                        autoComplete="address-level2"
                        {...form.getInputProps('city')}
                     />
                  </Grid.Col>
                  <Grid.Col span={4}>
                     <TextInput
                        label="State"
                        placeholder="CA"
                        autoComplete="address-level1"
                        {...form.getInputProps('state')}
                     />
                  </Grid.Col>
                  <Grid.Col span={4}>
                     <TextInput
                        label="ZIP"
                        placeholder="94102"
                        autoComplete="postal-code"
                        {...form.getInputProps('postalCode')}
                     />
                  </Grid.Col>
               </Grid>
            </ContentCard>
         </Stack>

         <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.9 }}
         >
            <Grid>
               <Grid.Col span={3}>
                  <Button
                     variant="secondary"
                     className="w-full"
                     onClick={() => getPreviousStep()}
                  >
                     <ArrowLeftMdIcon width={12} /> Back
                  </Button>
               </Grid.Col>
               <Grid.Col span={9}>
                  <Button
                     className="w-full"
                     onClick={() => {
                        const validation = form.validate()
                        if (!validation.hasErrors) {
                           handleSubmit(form.values)
                        }
                     }}
                     // disabled={!form.isValid()}
                  >
                     Continue <ChevronRightIcon width={18} />
                  </Button>
               </Grid.Col>
            </Grid>
         </motion.div>
      </Stack>
   )
}
